import Banner from "@/components/Banner";
import Button from "@/components/Button";
import EmptyState from "@/components/EmptyState";
import Progress from "@/components/Progress";
import type { LookupPersonInCcmsState } from "./useLookupPersonInCcms";

export const CCMS_LOOKUP_HELPER_TEXT =
  "This may be a PGA ID that exists in CCMS/SMS but has not yet been synced to Member 360.";

export type LookupPersonInCcmsViewProps = {
  pgaId: string;
  state: LookupPersonInCcmsState;
  onLookup: () => void;
  /** `emptyState` for the people table; `inline` for quick search. */
  variant?: "emptyState" | "inline";
};

function statusMessage(state: LookupPersonInCcmsState, pgaId: string): string | null {
  switch (state.phase) {
    case "not_in_ccms":
      return `PGA ID ${pgaId} was not found in CCMS.`;
    case "timeout":
      return `Sync was requested for PGA ID ${pgaId}, but the person is not available in Member 360 yet. Try again in a moment.`;
    case "error":
      return state.message;
    default:
      return null;
  }
}

function lookupButtonIcon(isLoading: boolean) {
  return isLoading ? <Progress size={18} color="currentColor" /> : "sync";
}

/**
 * Presentational CTA for looking up a missing PGA ID in CCMS.
 */
export default function LookupPersonInCcmsView({
  pgaId,
  state,
  onLookup,
  variant = "emptyState"
}: LookupPersonInCcmsViewProps) {
  const isLoading = state.phase === "loading";
  const message = statusMessage(state, pgaId);

  if (variant === "inline") {
    return (
      <div className="mt-3 mb-1 flex flex-col items-stretch gap-3 px-1" data-testid="lookup-person-in-ccms">
        <p className="text-body-medium text-sys-brand-on-surface-variant">{CCMS_LOOKUP_HELPER_TEXT}</p>
        <div className="flex justify-center">
          <Button variant="filledTonal" onClick={onLookup} disabled={isLoading} icon={lookupButtonIcon(isLoading)}>
            {isLoading ? "Looking in CCMS/SMS…" : "Look in CCMS/SMS"}
          </Button>
        </div>
        {message && <Banner inline variant="error" description="Lookup result" subText={message} className="w-full" />}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3" data-testid="lookup-person-in-ccms">
      <EmptyState
        iconName="person_search"
        title="Person not found in Member 360"
        caption={CCMS_LOOKUP_HELPER_TEXT}
        action={{
          children: isLoading ? "Looking in CCMS/SMS…" : "Look in CCMS/SMS",
          onClick: onLookup,
          disabled: isLoading,
          icon: lookupButtonIcon(isLoading)
        }}
      />
      {message && (
        <Banner inline variant="error" description="Lookup result" subText={message} className="w-full max-w-md" />
      )}
    </div>
  );
}
